/**
 * 线性图标集（lucide 风格）。
 *
 * 为什么自绘而不是引图标库：全部加起来 12 个图标，一个库的体积与 tree-shaking 风险
 * 都不值得；而且统一 1.5px 描边 + 24 网格这件事，自绘才控得住。
 *
 * 硬性规则：**禁止用 emoji 当图标**。emoji 的字形随系统/厂商变，
 * 大小与基线对不齐，深色底上还会自带白边，且无法跟随 currentColor。
 *
 * 统一规格：
 *  - 24×24 viewBox，几何画在 2..22 之间（四周留 2px 视觉边距）
 *  - `stroke="currentColor"` + `fill="none"`，颜色一律由父级文字色决定
 *  - 描边 1.5px，round 端点与拐角
 *  - `vector-effect="non-scaling-stroke"` 不用：图标尺寸都在 16..28，线宽差异肉眼不可见，
 *    而它在部分安卓 WebView 上会让描边渲染成 1px 硬线，反而更糊
 *
 * 无障碍：默认 `aria-hidden`（图标恒与文字同现，读屏读文字即可）。
 * 确实需要独立语义时传 `title`，组件会切成 `role="img"` 并挂 `<title>`。
 *
 * 注意 theme/global.css 把 `svg { pointer-events: none }` 设为全局默认：
 * 图标放进按钮里时，事件 target 永远是按钮本身，长按/拖拽判定不会被图标截胡。
 */

import type { ReactNode, SVGProps } from 'react'

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'children' | 'viewBox'> {
  /** 边长（px）。默认 20 —— 与 --fs-md 行高相称，放进 44px 命中区正好 */
  readonly size?: number
  /** 描边宽度。默认 1.5，与整套图标统一；只有超大号（>32）才建议降到 1.25 */
  readonly strokeWidth?: number
  /** 传了就切成 role="img" 并挂 <title>；不传则 aria-hidden */
  readonly title?: string
}

/** 所有图标共用的外壳：把规格收敛到一处，单个图标只写路径 */
function Svg({ size = 20, strokeWidth = 1.5, title, children, ...rest }: IconProps & { children: ReactNode }): ReactNode {
  const labelled = title !== undefined && title !== ''
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      focusable="false"
      {...(labelled ? { role: 'img' } : { 'aria-hidden': true })}
      {...rest}
    >
      {labelled && <title>{title}</title>}
      {children}
    </svg>
  )
}

/** 玩家 / 座位 */
export function IconUser(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <circle cx="12" cy="8" r="3.75" />
      <path d="M4.5 20.25a7.5 7.5 0 0 1 15 0" />
    </Svg>
  )
}

/** 筹码（叠起来的三枚，侧视）。用于筹码量、底池、买入 */
export function IconChips(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <ellipse cx="12" cy="7" rx="7.5" ry="3.25" />
      <path d="M4.5 7v4.5c0 1.8 3.36 3.25 7.5 3.25s7.5-1.45 7.5-3.25V7" />
      <path d="M4.5 11.5V16c0 1.8 3.36 3.25 7.5 3.25s7.5-1.45 7.5-3.25v-4.5" />
    </Svg>
  )
}

/** 复制（房间码） */
export function IconCopy(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <rect x="9" y="9" width="11" height="11" rx="2.5" />
      <path d="M15 6.25V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h.25" />
    </Svg>
  )
}

/** 分享 / 邀请 */
export function IconShare(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M12 3.5v11" />
      <path d="M8.25 7.25 12 3.5l3.75 3.75" />
      <path d="M5.5 13.5v5a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2v-5" />
    </Svg>
  )
}

/** 退出房间 / 离开牌桌 */
export function IconExit(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M14.5 4.5h3a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2h-3" />
      <path d="M10 8.25 13.75 12 10 15.75" />
      <path d="M13.75 12H4.5" />
    </Svg>
  )
}

/** 设置。用推子而不是齿轮：齿轮在 16px 下会糊成一坨，推子的三条线更清楚 */
export function IconSettings(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M4 7h4.5M13.5 7H20" />
      <path d="M4 17h7.5M16.5 17H20" />
      <circle cx="11" cy="7" r="2.5" />
      <circle cx="14" cy="17" r="2.5" />
    </Svg>
  )
}

/** 新增（建房 / 加座 / 补码） */
export function IconPlus(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M12 5v14M5 12h14" />
    </Svg>
  )
}

/** 确认 / 已完成 */
export function IconCheck(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M4.5 12.5 9.5 17.5 19.5 6.5" />
    </Svg>
  )
}

/** 关闭 / 取消 */
export function IconClose(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M6 6l12 12M18 6L6 18" />
    </Svg>
  )
}

/** 牌桌聊天入口：对话框里三个点 */
export function IconChat(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M5.5 5.25h13a2 2 0 0 1 2 2v7.5a2 2 0 0 1-2 2H11l-4.25 3.25V16.75H5.5a2 2 0 0 1-2-2v-7.5a2 2 0 0 1 2-2z" />
      <path d="M8.5 11h.01M12 11h.01M15.5 11h.01" />
    </Svg>
  )
}

/** 发送 */
export function IconSend(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M4.5 11.5 19.5 4.5l-5 15-3-6.25z" />
      <path d="M11.5 13.25 19.5 4.5" />
    </Svg>
  )
}

/** 倒计时 / 行动时限 */
export function IconClock(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="8.25" />
      <path d="M12 7.25V12l3 1.75" />
    </Svg>
  )
}

/** 断线。用于顶部断线条与座位的离线标记 */
export function IconWifiOff(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <path d="M3 3l18 18" />
      <path d="M9.2 15.2a4 4 0 0 1 5.6 0" />
      <path d="M6.1 12.1a8.4 8.4 0 0 1 3.3-2.05" />
      <path d="M17.9 12.1a8.4 8.4 0 0 0-3.5-2.1" />
      <path d="M3.2 8.9a13 13 0 0 1 4.2-2.55" />
      <path d="M20.8 8.9a13 13 0 0 0-9.6-3.35" />
      <path d="M12 18.5h.01" />
    </Svg>
  )
}

/**
 * 庄家钮 D。
 * 字母用 <text> 而不是路径：路径化的 D 在 16px 下描边会糊，
 * 而 text 走系统字形渲染（含 hinting），小尺寸反而更清楚。填充色跟随 currentColor。
 */
export function IconDealer({ size = 20, strokeWidth = 1.5, title, ...rest }: IconProps): ReactNode {
  return (
    <Svg size={size} strokeWidth={strokeWidth} {...(title === undefined ? {} : { title })} {...rest}>
      <circle cx="12" cy="12" r="8.75" />
      <text
        x="12"
        y="12"
        textAnchor="middle"
        dominantBaseline="central"
        fill="currentColor"
        stroke="none"
        fontSize="11"
        fontWeight="700"
        fontFamily="var(--ff-num)"
      >
        D
      </text>
    </Svg>
  )
}

/** 钥匙。账号找回相关的面板标题用它。 */
export function IconKey(props: IconProps): ReactNode {
  return (
    <Svg {...props}>
      <circle cx="8.75" cy="8.75" r="4.75" />
      <path d="M12.1 12.1 20.5 20.5" />
      <path d="M17 17l-2 2" />
    </Svg>
  )
}
